<script setup lang="ts">
    import PlanManualDownloadWarningModal from '@/views/plan/PlanManualDownloadWarningModal.vue';
    import PlanOrderSurgicalGuide from '@/views/plan/PlanOrderSurgicalGuide.vue';
    import type { ApiPlan, ApiPlansCase } from '@/views/plan/api/types';
    import { PLANS_TARGETS } from '@/domain/analytics/analyticsTargets';
    import { usePlanActionsViewModel } from '@/views/plan/usePlanActionsViewModel';

    const props = defineProps<Props>();

    export interface Props {
        project: ApiPlansCase;
        plan: ApiPlan;
    }

    // Destructured so the template binds the values rather than the refs: a plain composable
    // returns raw refs, which Vue does not auto-unwrap through an object, but does unwrap as
    // top-level bindings of `<script setup>`.
    const {
        creation,
        isDownloadingPdf,
        isDownloadingModels,
        showWarningDialog,
        showReportDownload,
        showModelsDownload,
        showOrderGuide,
        showDownloadWarning,
        requestReportDownload,
        downloadFromWarning,
        closeWarning,
        downloadModels,
    } = usePlanActionsViewModel(
        () => props.plan,
        () => props.project,
    );
</script>

<template>
    <div class="app-plan-actions">
        <div class="app-plan-download-buttons d-flex align-center">
            <app-button-secondary
                v-if="showModelsDownload"
                class="mt-2"
                :loading="isDownloadingModels"
                data-testid="download-raw-models-button"
                :data-analytics-id="PLANS_TARGETS.rawButton"
                @click="downloadModels"
            >
                Download Raw Models
            </app-button-secondary>
            <v-spacer v-if="showModelsDownload" />

            <PlanOrderSurgicalGuide
                v-if="showOrderGuide"
                :data-analytics-id="PLANS_TARGETS.psiButton"
                :loading="creation !== 'ready'"
                :case-id="props.project.id"
                :plan-id="props.plan.id"
            />

            <v-spacer />
            <app-button
                v-if="showReportDownload"
                :loading="isDownloadingPdf"
                data-testid="download-pdf-button"
                :data-analytics-id="PLANS_TARGETS.downloadReportButton"
                @click="requestReportDownload"
            >
                Download PDF
            </app-button>
            <v-card
                v-else-if="creation !== 'ready'"
                width="244"
                rounded="lg"
                variant="tonal"
                class="px-4 py-2 text-secondary text-caption d-block"
            >
                <div v-if="creation === 'creating'">
                    <v-progress-linear color="primary" class="mt-2" indeterminate height="6" />
                    <br />
                    Your downloadable Plan is being created, this process can take up to 10 minutes.
                </div>
                <div v-else>
                    <v-icon color="error" size="40" class="w-100 mb-5 d-flex">
                        mdi-alert-circle-outline
                    </v-icon>
                    <span class="small-text text-error">
                        There was an error creating your Plan
                    </span>
                    <span class="small-text text-planner-grey">
                        Please
                        <a href="https://support.formuslabs.com/hc/en-us" target="_blank">
                            contact support
                        </a>
                        for assistance
                    </span>
                </div>
            </v-card>

            <PlanManualDownloadWarningModal
                v-if="showDownloadWarning"
                :value="showWarningDialog"
                @close="closeWarning"
                @submit="downloadFromWarning"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
    .app-plan-actions {
        flex-basis: 260px;
        flex-grow: 0;
    }

    .app-plan-card-ref {
        color: var(--planner-grey-2);
        text-transform: uppercase;
        font-weight: 700;
        font-size: 0.75rem;
        margin-bottom: 20px;
    }

    .app-plan-card-ref-number {
        color: rgb(var(--v-theme-secondary));
    }
</style>
